import { ExpandMore } from '@mui/icons-material'
import { TabListProps } from '@mui/lab'
import {
  Box,
  Button,
  Chip,
  ChipProps,
  Container,
  Divider,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Select,
  SelectProps,
  Stack,
  Table,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import dayjs, { Dayjs } from 'dayjs'
import { FC, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { Api } from '../../api'
import { DatePicker } from '../../components/DatePicker'
import { Fa } from '../../components/Fa'
import { Header } from '../../components/Header'
import { Modal } from '../../components/Modal'
import { SessionSummary } from '../../components/SessionSummary'
import { StyledTab, StyledTabs } from '../../components/styled'
import { UserCard } from '../../components/UserCard'
import { Weekdays } from '../../components/Weekdays'
import { toStandardDateString } from '../../helpers/date'
import { createDataset, dedup } from '../../helpers/others'
import { useGet } from '../../hooks/get'
import { useSign } from '../../hooks/sign'
import { useUser } from '../../hooks/user'
import { SessionService } from '../../services/session'
import { themeMeta } from '../../theme/meta'

const Classes: FC = () => {
  const { user } = useUser()

  const { t } = useTranslation()
  const theme = useTheme()
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))

  const [selectedDate, setSelectedDate] = useState<string>(
    toStandardDateString(dayjs().tz())
  )
  const [selectedType, setSelectedType] = useState<string>()
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [sessions, setSessions] = useState<Dto.MarketSession[]>()
  const [selectedSessionId, setSelectedSessionId] = useState<number>()
  const [pendingSessionId, setPendingSessionId] = useState<number>()

  const { data: weekSessions, mutate: mutateWeekSessions } = useGet<
    Dto.MarketSession[]
  >([
    'member_portal/sessions',
    {
      date: selectedDate,
      range: 'week',
      ...(user && { member_id: user.memberId }),
    },
  ])

  const sign = useSign()

  const createHandleCategoryDelete: (
    category: string
  ) => ChipProps['onDelete'] = (category) => () => {
    setSelectedCategories((prev) => {
      const next = [...prev]
      next.splice(prev.indexOf(category), 1)
      return next
    })
  }

  const createHandleSessionClick = (id: number) => () => {
    setSelectedSessionId(id)
  }

  const createHandleBookClick = (sessionId: number) => async () => {
    if (!sessions) {
      return
    }

    if (!user) {
      const session = sessions.find((session) => session.id === sessionId)
      await sign.start({
        session,
        categoryIds: [session!.gym_category_id],
      })
    }

    try {
      setPendingSessionId(sessionId)
      await Api.post(
        'bookings',
        { session_id: sessionId },
        { memberAuth: true }
      )
      await mutateWeekSessions()
      toast.success(t("You're all booked in!"))
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message)
      }
    } finally {
      setPendingSessionId(undefined)
    }
  }

  const createHandleCancelClick = (session: Dto.MarketSession) => async () => {
    try {
      if (!session.my_booking) {
        return
      }

      const cancelled = await SessionService.cancelBooking(
        session.my_booking,
        session,
        {
          onBeforeRequest: () => {
            setPendingSessionId(session.id)
            handleDetailsClose()
          },
        }
      )

      if (!cancelled) {
        return
      }

      await mutateWeekSessions()
      setPendingSessionId(undefined)
    } catch (error) {
      setPendingSessionId(undefined)

      if (error instanceof Error) {
        toast.error(error.message)
      }
    }
  }

  const handleDateChange: (value: string | Dayjs) => void = (value) => {
    setSelectedDate(toStandardDateString(value))
    setSelectedType(undefined)
    setSelectedCategories([])
  }

  const handleTypeChange: TabListProps['onChange'] = (e, value) => {
    setSelectedType(value)
    setSelectedCategories([])
  }

  const handleCategoriesChange: SelectProps['onChange'] = (e) => {
    setSelectedCategories(e.target.value as string[])
  }

  const handleClearFilterClick = () => {
    setSelectedCategories([])
  }

  const handleDetailsClose = () => {
    setSelectedSessionId(undefined)
  }

  const startDate = dayjs().tz().day(0)
  const availableDates = Array.from({ length: 7 * 4 }, (n, index) =>
    toStandardDateString(startDate.add(index, 'day'))
  )
  const daySessions = sessions
    ?.sort(
      (a, b) =>
        dayjs(a.session_start_time_utc).valueOf() -
        dayjs(b.session_start_time_utc).valueOf()
    )
    .filter(
      (session) =>
        toStandardDateString(dayjs(session.session_start_time_utc).tz()) ===
        selectedDate
    )
  const types = dedup(
    daySessions
      ?.map((session) => {
        if (session.gym_category.category.type === 'Other') {
          return session.gym_category.category.name
        }

        return session.gym_category.category.type
      })
      .sort() || []
  )
  const uiType = selectedType || types[0]
  const categories = dedup(
    daySessions
      ?.filter((session) => session.gym_category.category.type === uiType)
      .map((session) => session.gym_category.category.name) || []
  )
  const selectedSession = sessions?.find(
    (session) => session.id === selectedSessionId
  )

  useEffect(() => {
    setSessions((prev) =>
      dedup([...(weekSessions || []), ...(prev || [])], 'id')
    )
  }, [weekSessions])

  return (
    <>
      <Helmet>
        <title>{t('Classes')}</title>
      </Helmet>
      <Header />
      <Container>
        <Box py={5}>
          <Typography variant="h1" fontSize="1.8em" lineHeight={1.1} mb={5}>
            {t('Your next class is waiting for you.')}
          </Typography>
          <Grid container alignItems="center" spacing={2} wrap="nowrap" mb={4}>
            <Grid size="grow">
              <Weekdays
                dates={availableDates}
                value={selectedDate}
                onChange={handleDateChange}
              />
            </Grid>
            <Grid>
              <DatePicker
                trigger={
                  <IconButton
                    sx={{
                      p: 2,
                      border: `2px solid ${theme.palette.divider}`,
                      fontSize: '1.2rem',
                    }}
                  >
                    <Fa name="calendar" size="md" color="secondary" />
                  </IconButton>
                }
                minDate={startDate}
                maxDate={startDate.add(7 * 4 - 1, 'day')}
                value={dayjs(selectedDate)}
                onChange={handleDateChange}
              />
            </Grid>
          </Grid>
          <StyledTabs value={uiType} onChange={handleTypeChange}>
            {types.map((type) => (
              <StyledTab key={type} label={type} value={type}></StyledTab>
            ))}
          </StyledTabs>
          {!!categories.length && (
            <Box mb={2}>
              <Grid container alignItems="center" spacing={1}>
                <Grid>
                  <Select
                    value={selectedCategories}
                    multiple
                    native={false}
                    displayEmpty
                    IconComponent={ExpandMore}
                    renderValue={(value) =>
                      [
                        t('Class types'),
                        ...(value.length ? [`(${value.length})`] : []),
                      ].join(' ')
                    }
                    onChange={handleCategoriesChange}
                  >
                    {categories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                {!!selectedCategories.length && (
                  <Grid container alignItems="inherit">
                    {selectedCategories.map((category) => (
                      <Grid key={category}>
                        <Chip
                          label={category}
                          onDelete={createHandleCategoryDelete(category)}
                        />
                      </Grid>
                    ))}
                    <Grid>
                      <Typography variant="h5">
                        <Link
                          component="button"
                          color="textPrimary"
                          onClick={handleClearFilterClick}
                        >
                          {t('Clear filter')}
                        </Link>
                      </Typography>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
          <Table
            sx={{
              borderCollapse: 'separate',
              borderSpacing: `0 ${theme.spacing(1.5)}`,

              '& .MuiTableRow-root': {
                '&:hover': {
                  backgroundColor: themeMeta.colors.monochrome.offWhite,
                },
                '&[data-unavailable]': {
                  opacity: 0.5,
                  pointerEvents: 'none',
                },
              },
              '& .MuiTableCell-root': {
                py: 3,
                border: `1px solid ${theme.palette.divider}`,

                '&:not(:first-child):not(:last-child)': {
                  borderInlineColor: 'transparent',
                },
                '&:first-child': {
                  borderInlineEndColor: 'transparent',
                  borderRadius: `${theme.spacing(1)} 0 0 ${theme.spacing(1)}`,
                },
                '&:last-child': {
                  borderInlineStartColor: 'transparent',
                  borderRadius: `0 ${theme.spacing(1)} ${theme.spacing(1)} 0`,
                },
              },
            }}
          ></Table>
          <Grid container direction="column" spacing={2}>
            {daySessions
              ?.filter(
                (session) =>
                  [
                    session.gym_category.category.type,
                    session.gym_category.category.name,
                  ].includes(uiType) &&
                  (!selectedCategories.length ||
                    selectedCategories.includes(
                      session.gym_category.category.name!
                    ))
              )
              .map((session) => {
                const unavailable =
                  dayjs().isAfter(session.session_start_time_utc) ||
                  session.bookings_count >=
                    session.member_limit + session.waiting_list
                const waitlist =
                  session.bookings_count - session.confirmed_bookings_count
                return (
                  <Grid
                    key={session.id}
                    container
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ sm: 'center' }}
                    spacing={1}
                    p={3}
                    {...createDataset({
                      unavailable,
                    })}
                    sx={{
                      border: `2px solid ${theme.palette.divider}`,
                      borderRadius: theme.spacing(1),

                      '&:hover': {
                        backgroundColor: themeMeta.colors.monochrome.offWhite,
                      },
                      '&[data-unavailable]': {
                        opacity: 0.5,
                        pointerEvents: 'none',
                      },
                    }}
                  >
                    <Grid size={{ sm: 2 }}>
                      <Typography>
                        {[
                          dayjs(session.session_start_time_utc)
                            .tz()
                            .format('HH:mm'),
                          dayjs(session.session_end_time_utc)
                            .tz()
                            .format('HH:mm'),
                        ].join(' ~ ')}
                      </Typography>
                    </Grid>
                    <Grid
                      size={{ sm: 'grow' }}
                      container
                      justifyContent={{ xs: 'space-between' }}
                      alignItems="center"
                    >
                      <Grid size="grow">
                        <Typography
                          variant="h4"
                          fontWeight={600}
                          sx={{ textDecoration: 'underline' }}
                        >
                          <Link
                            component="button"
                            color="inherit"
                            onClick={createHandleSessionClick(session.id)}
                            sx={{ textAlign: 'start' }}
                          >
                            {session.gym_category.category.name}
                          </Link>
                        </Typography>
                      </Grid>
                      <Grid size={{ sm: 'grow' }}>
                        <Chip
                          label={
                            <>
                              {session.confirmed_bookings_count}/
                              {session.member_limit}
                              {!!waitlist && ` (${waitlist})`}
                            </>
                          }
                        ></Chip>
                      </Grid>
                    </Grid>
                    <Grid
                      size={{ sm: 'grow' }}
                      container
                      justifyContent={{ xs: 'space-between' }}
                    >
                      <Grid size="grow">
                        <UserCard user={session.gym_staff.user} />
                      </Grid>
                      <Grid size={{ sm: 'grow' }}>
                        <Typography variant="body2" color="textSecondary">
                          {session.gym_room?.name || '-'}
                        </Typography>
                      </Grid>
                    </Grid>
                    <Grid>
                      <Box mt={{ xs: 2, sm: 0 }}>
                        {session.my_booking ? (
                          <Button
                            variant="outlined"
                            size="small"
                            fullWidth={downSm}
                            loading={session.id === pendingSessionId}
                            onClick={createHandleCancelClick(session)}
                          >
                            {session.my_booking.is_confirmed
                              ? t('Cancel booking')
                              : t('Cancel waitlist')}
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            fullWidth={downSm}
                            loading={session.id === pendingSessionId}
                            onClick={createHandleBookClick(session.id)}
                          >
                            {unavailable
                              ? t('Unavailable')
                              : SessionService.isFull(session)
                              ? t('Join waitlist')
                              : t('Book')}
                          </Button>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                )
              })}
          </Grid>
        </Box>
        <Modal
          open={!!selectedSession}
          fullWidth
          actions={
            selectedSession &&
            (selectedSession.my_booking
              ? [
                  {
                    variant: 'outlined',
                    loading: selectedSession.id === pendingSessionId,
                    children: selectedSession.my_booking.is_confirmed
                      ? t('Cancel booking')
                      : t('Cancel waitlist'),
                    onClick: createHandleCancelClick(selectedSession),
                  },
                ]
              : [
                  {
                    loading: selectedSession.id === pendingSessionId,
                    children: SessionService.isFull(selectedSession)
                      ? t('Join waitlist')
                      : t('Book'),
                    onClick: createHandleBookClick(selectedSession.id),
                  },
                ])
          }
          onClose={handleDetailsClose}
        >
          {selectedSession && (
            <Stack spacing={4}>
              <Stack spacing={1}>
                <Typography variant="h1">
                  {selectedSession.gym_category.category.name}
                </Typography>
                <Box>
                  <Chip label={selectedSession.gym_category.category.type} />
                </Box>
                <Typography
                  variant="body2"
                  color={themeMeta.colors.monochrome.label}
                >
                  {t('{{spots}} left', {
                    spots: t('spotWithCount', {
                      count: Math.max(
                        selectedSession.member_limit -
                          selectedSession.bookings_count,
                        0
                      ),
                    }),
                  })}
                  {selectedSession.bookings_count >
                    selectedSession.confirmed_bookings_count && (
                    <>
                      <Divider
                        component="span"
                        orientation="vertical"
                        sx={{ mx: 1 }}
                      />
                      {t('{{number}} waitlisted', {
                        number:
                          selectedSession.bookings_count -
                          selectedSession.confirmed_bookings_count,
                      })}
                    </>
                  )}
                </Typography>
              </Stack>
              <SessionSummary session={selectedSession} name={false} />
              <Stack spacing={1}>
                <Typography variant="h4">{t('Description')}</Typography>
                <Typography>{selectedSession.description}</Typography>
              </Stack>
            </Stack>
          )}
        </Modal>
      </Container>
      {sign.element}
    </>
  )
}

export { Classes }
